import locale from "csi/locale";
import { ILocaleProvider } from "designSystem/locale";
import _ from "lodash";
import { IAggregateStoreGroup, IAggregateStoreGroupInfo, IAggregateStoreResponse, IAggregateStoreResult } from "src/c8-platform/type/aggregateStore";
import { aggregateStoreResponseHasMultipleKeys, getAggregateStoreResultKeysForGroup, isAggregateStoreResponseGrouped } from "src/c8-platform/service/aggregateStoreService";
import { C8NodeType } from "src/c8-platform/type/node";
import { NamesResult } from "./Names";
import { StoreNames } from "csi/store/Names";

const CSS_RESULT = "csi-result-selection";

export const DISABLED_PROPERTY = "_disabled";
export const IS_GROUP_PROPERTY = "_isGroup";
export const EMPTY_GROUP_ITEM_PROPERTY = "_emptyGroup";
export const INVALID_PROPERTY = "_invalid";

/** assign index in items array to each item */
export function assignItemIndex(items: NamesResult[]): void {
	items.forEach((item, i) => {
		item.index = i;
	});
}

/** return CSS to apply to result items of Names data store */
export function getNamesResultItemCss(customCss = ""): string {
	return customCss ? `${CSS_RESULT} ${customCss}` : CSS_RESULT;
}

/** create item to designate an empty aggregate group */
export function createEmptyGroupItem(groupId: string): NamesResult {
	const name = locale.resource("SEARCH_NO_RESULTS");

	return {
		$: `Empty_${groupId}`,
		Name: name,
		css: getNamesResultItemCss("csi-grouped-selection-empty"),
		group: groupId,
		search: name,
		_disabled: true,
		_emptyGroup: true,
	};
}

/** return true if slim card should be used to render store results */
export function doesStoreUseSlimCard(store: StoreNames): boolean {
	const aggregateStoreResponse = store?.aggregateStoreResponse;
	return !!aggregateStoreResponse && aggregateStoreResponseHasMultipleKeys(aggregateStoreResponse);
}

/** map results of one aggregate store group to items for a store */
export function mapResultsToGroupItems(response: IAggregateStoreResponse, results: IAggregateStoreResult[], group?: IAggregateStoreGroup): NamesResult[] {
	const { disabledIds } = response;
	const [key1, key2] = getAggregateStoreResultKeysForGroup(response, group);
	const css = CSS_RESULT;
	const emptyName = locale.getEmptyName();
	const groupId = group?.groupId;

	return results.map((result): NamesResult => {
		const { $id } = result;
		const Name = result[key1] || emptyName;
		const optional: Partial<NamesResult> = {};

		if (key2) {
			optional.name2 = result[key2] || emptyName;
		}

		if (disabledIds?.has($id)) {
			optional._disabled = true;
		}

		const search = optional.name2 ? `${Name} | ${optional.name2}` : Name;

		return {
			$: $id,
			Name,
			css,
			group: groupId,
			search,
			...optional,
		};
	});
}

/** convert results returned from aggregate store query into items for a store */
export function getNamesFromAggregateStoreResponse(response: IAggregateStoreResponse, forceEmptyItem = false): NamesResult[] {
	const groupsItems = response.groups.map((group) => {
		const {
			groupId,
			results = [],
		} = group;

		if (!results.length) {
			return forceEmptyItem ? [createEmptyGroupItem(groupId)] : [];
		}

		return mapResultsToGroupItems(response, results, group);
	});

	const items = _.flatten(groupsItems);
	assignItemIndex(items);
	return items;
}

/** 
 * Get a localized node type. If not available, return a localized 'Other Items'
 * @param nodeType - The node type to localize.
 */
export function getNodeTypeLabel(nodeType: C8NodeType, useOther: boolean): string {
	return useOther ?
		(
			nodeType ?
				locale.resource("OtherAggregateStoreGroup", [locale.typePlural(nodeType)]) :
				locale.resource("OtherAggregateStoreGroupDefault")
		) :
		locale.typePlural(nodeType);
}

/** Get a localized node type. If not available, return a localized 'Other Items'
 * @param nodeType - The node type to localize.
 * @param loc - The locale provider instance.
 * @param useOther - indicates if the label must be prefixed with 'Other'.
 */
function getNodeTypeLabelWithLocaleProvider(nodeType: C8NodeType, useOther: boolean, loc: ILocaleProvider): string {
	return useOther ?
		(
			nodeType ?
				loc.resource("OtherAggregateStoreGroup", [loc.type(nodeType, true)]) :
				loc.resource("OtherAggregateStoreGroupDefault")
		) :
		loc.type(nodeType, true);
}

/** return localized group's label. The value will have this format:
 * - '<Localized label resource>' if labelResource is available.
 * - 'Other <node types>' if labelResource is not available.
 * - 'Other Items' if nodeType is not available.
 * @param group - the group whose label this function will localize.
 */
export function getLocalizedGroupLabel(group: IAggregateStoreGroupInfo): string {
	const { labelResource, nodeType, notFirstOfType } = group;
	return labelResource ? locale.resource(labelResource) : getNodeTypeLabel(nodeType, notFirstOfType);
}

/** return localized label for aggregate store group using React locale provider */
export function getLocalizedGroupLabelWithLocaleProvider(aggregateGroup: IAggregateStoreGroupInfo, loc: ILocaleProvider): string {
	const { labelResource, nodeType, notFirstOfType } = aggregateGroup;

	// TODO - should have the ability to customize the label
	// hard-code the type for now for SupplierFactory
	const finalNodeType = (nodeType === "SupplierFactory" ? "Factory" : nodeType);
	return labelResource ? loc.resource(labelResource) : getNodeTypeLabelWithLocaleProvider(finalNodeType, notFirstOfType, loc);
}

/** determine whether or not store items should be grouped */
export function shouldGroupStoreItems(store: StoreNames): boolean {
	const aggregateStoreResponse = store?.aggregateStoreResponse;
	return store?.hasPriorityItems?.() && (!aggregateStoreResponse || isAggregateStoreResponseGrouped(aggregateStoreResponse));
}

/** return list of groups which should be suggested to user  */
export function getStoreSuggestedGroups(store: StoreNames): string[] {
	const aggregateStoreResponse = store?.aggregateStoreResponse;
	const shouldGroupItems = shouldGroupStoreItems(store);
	if (aggregateStoreResponse && shouldGroupItems) {
		const { groups } = aggregateStoreResponse;
		return groups.filter((item) => item.suggested).map((item) => getLocalizedGroupLabel(item));
	}
	return [];
}

/** return localized group's label when using old client-side aggregate store. The value will have this format:
 * - '<Localized group name>' if useOther is false.
 * - 'Other <node types>' if useOther is true and typeNode is available.
 * - 'Other Items' if useOther is true and typeNode is not available.
 * @param groupName
 * @param typeNode
 * @param useTypeNode - Whether to use groupName or typeNode
 * @param isMoreThanOneGroup - if this is true, and useTypeNode is also true, 'Other' should be used as a prefix.
 */
export function getLocalizedGroupLabelOld(groupName: string, typeNode: C8NodeType, useTypeNode: boolean, isMoreThanOneGroup: boolean): string {
	return (useTypeNode) ? getNodeTypeLabel(typeNode, isMoreThanOneGroup) : locale.resource(groupName);
}
